/*
    The bordered status pill used for Confidence tier, SRM check and Statistical power
    (MPN-1688, brought in line with the approved prototype).

    These were coloured text. A tier is the column people scan a wide table for, and text in
    `text-warning` next to text in `text-muted` is hard to pick out at a glance and impossible
    to tell apart in greyscale or with a red-green colour deficiency. So: a shape, a dot and a
    tone, and a glyph on the two labels that carry a warning.

    Caution reads as a warning rather than as a gentler Proven, deliberately. It fires when
    the control converted nobody, which makes the relative lift undefined and the result
    unsafe to quote, and the one thing that must never happen is somebody taking it for a
    weaker kind of success.
*/
export type PillTone = "ok" | "pause" | "neutral" | "danger";

export default function StatusPill({ label, tone }: { label: string; tone: PillTone }) {
    return (
        <span className={`holdout-report__pill holdout-report__pill--${tone}`}>
            <span className="holdout-report__pill-dot" />
            {label}
        </span>
    );
}

/** Confidence tier. Unknown values render as themselves rather than vanishing. */
export function TierPill({ tier }: { tier: string }) {
    const known = TIER_PILL[tier];
    return <StatusPill label={known?.label ?? tier} tone={known?.tone ?? "neutral"} />;
}

/**
 * Sample Ratio Mismatch.
 *
 * A missing value renders as a dash, not as a pass. `overall()` on the server does not run an
 * SRM check at all, so the previous "anything that is not fail is Pass" test drew a green
 * Pass on the portfolio row for a test that had never been performed. On a report whose
 * entire job is saying which numbers can be trusted, that is the worst available default.
 */
export function SrmPill({ check }: { check?: string | null }) {
    if (!check) return <span className="is-empty">-</span>;
    return check === "fail"
        ? <StatusPill label="Fail !" tone="danger" />
        : <StatusPill label="Pass" tone="ok" />;
}

/** Statistical power. */
export function PowerPill({ power }: { power?: string | null }) {
    // "unknown" is the server saying it could not tell (an empty arm), not a pass.
    if (!power || power === "unknown") return <span className="is-empty">-</span>;
    return power === "low"
        ? <StatusPill label="Low" tone="pause" />
        : <StatusPill label="Sufficient" tone="ok" />;
}

const TIER_PILL: Record<string, { label: string; tone: PillTone }> = {
    proven: { label: "Proven ✓", tone: "ok" },
    directional: { label: "Directional", tone: "pause" },
    insufficient: { label: "Insufficient", tone: "neutral" },
    caution: { label: "Caution !", tone: "danger" },
};
